<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import DealsAPI from 'dashboard/api/deals';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  deal: { type: Object, default: null },
  stages: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved', 'deleted']);
const { t } = useI18n();

const dialogRef = ref(null);
const isSaving = ref(false);
const isDeleting = ref(false);
const isAddingComment = ref(false);
const isSearchingContacts = ref(false);
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const comment = ref('');
const dealDetails = ref(null);
let contactSearchTimer;
let contactSearchController;

const localDate = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const emptyForm = () => ({
  title: '',
  deal_stage_id: props.stages[0]?.id || '',
  assignee_id: '',
  value: 0,
  currency: 'BRL',
  status: 'open',
  started_on: localDate(),
  expected_close_on: '',
  closed_on: '',
  valid_until: '',
  description: '',
});

const form = ref(emptyForm());
const isEditing = computed(() => Boolean(dealDetails.value?.id));
const dialogTitle = computed(() =>
  isEditing.value ? t('DEALS.FORM.EDIT_TITLE') : t('DEALS.FORM.CREATE_TITLE')
);
const isFormInvalid = computed(
  () =>
    !form.value.title.trim() ||
    !selectedContact.value?.id ||
    !form.value.deal_stage_id
);

const resetForm = deal => {
  dealDetails.value = deal;
  contactQuery.value = '';
  contactResults.value = [];
  selectedContact.value = deal?.contact || null;
  comment.value = '';
  form.value = deal
    ? {
        title: deal.title || '',
        deal_stage_id: deal.deal_stage_id,
        assignee_id: deal.assignee_id || '',
        value: Number(deal.value || 0),
        currency: deal.currency || 'BRL',
        status: deal.status || 'open',
        started_on: deal.started_on || localDate(),
        expected_close_on: deal.expected_close_on || '',
        closed_on: deal.closed_on || '',
        valid_until: deal.valid_until || '',
        description: deal.description || '',
      }
    : emptyForm();
};

watch(() => props.deal, resetForm, { immediate: true });

watch(contactQuery, query => {
  clearTimeout(contactSearchTimer);
  contactSearchController?.abort();
  contactResults.value = [];
  isSearchingContacts.value = false;
  if (query.trim().length < 2) return;

  contactSearchTimer = setTimeout(async () => {
    contactSearchController = new AbortController();
    isSearchingContacts.value = true;
    try {
      const { data } = await ContactsAPI.search(query.trim(), 1, 'name', '', {
        signal: contactSearchController.signal,
      });
      contactResults.value = data.payload || [];
    } catch (error) {
      if (error.name !== 'CanceledError') {
        contactResults.value = [];
      }
    } finally {
      isSearchingContacts.value = false;
    }
  }, 300);
});

onBeforeUnmount(() => {
  clearTimeout(contactSearchTimer);
  contactSearchController?.abort();
});

const selectContact = contact => {
  selectedContact.value = contact;
  contactQuery.value = '';
  contactResults.value = [];
};

const nullablePayload = values =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value === '' ? null : value,
    ])
  );

const saveDeal = async () => {
  if (isFormInvalid.value) {
    useAlert(t('DEALS.FORM.REQUIRED_ERROR'));
    return;
  }

  isSaving.value = true;
  const payload = {
    deal: nullablePayload({
      ...form.value,
      contact_id: selectedContact.value.id,
    }),
  };

  try {
    if (isEditing.value) {
      await DealsAPI.update(dealDetails.value.id, payload);
      useAlert(t('DEALS.FORM.UPDATE_SUCCESS'));
    } else {
      await DealsAPI.create(payload);
      useAlert(t('DEALS.FORM.CREATE_SUCCESS'));
    }
    dialogRef.value.close();
    emit('saved');
  } catch {
    useAlert(t('DEALS.FORM.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteDeal = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('DEALS.FORM.DELETE_CONFIRM'))) return;

  isDeleting.value = true;
  try {
    await DealsAPI.delete(dealDetails.value.id);
    useAlert(t('DEALS.FORM.DELETE_SUCCESS'));
    dialogRef.value.close();
    emit('deleted');
  } catch {
    useAlert(t('DEALS.FORM.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

const reloadDetails = async () => {
  const { data } = await DealsAPI.show(dealDetails.value.id);
  dealDetails.value = data;
};

const addComment = async () => {
  if (!comment.value.trim()) return;

  isAddingComment.value = true;
  try {
    await DealsAPI.createComment(dealDetails.value.id, {
      deal_comment: { content: comment.value.trim() },
    });
    comment.value = '';
    await reloadDetails();
    useAlert(t('DEALS.COMMENTS.SUCCESS'));
  } catch {
    useAlert(t('DEALS.COMMENTS.ERROR'));
  } finally {
    isAddingComment.value = false;
  }
};

const formatDateTime = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const activityLabel = action => {
  const labels = {
    created: t('DEALS.ACTIVITIES.CREATED'),
    updated: t('DEALS.ACTIVITIES.UPDATED'),
    stage_changed: t('DEALS.ACTIVITIES.STAGE_CHANGED'),
    automation_executed: t('DEALS.ACTIVITIES.AUTOMATION_EXECUTED'),
    commented: t('DEALS.ACTIVITIES.COMMENTED'),
  };
  return labels[action];
};

const activityActor = activity => {
  if (activity.metadata?.automation_rule_name) {
    return t('DEALS.ACTIVITIES.AUTOMATION_ACTOR', {
      name: activity.metadata.automation_rule_name,
    });
  }
  return activity.actor?.display_name || t('DEALS.COMMON.UNKNOWN_USER');
};

const open = () => {
  resetForm(props.deal);
  dialogRef.value.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="dialogTitle"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div class="flex max-h-[75vh] flex-col gap-6 overflow-y-auto pr-1">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          v-model="form.title"
          class="md:col-span-2"
          :label="t('DEALS.FORM.TITLE')"
          :placeholder="t('DEALS.FORM.TITLE_PLACEHOLDER')"
          required
        />

        <div class="flex flex-col gap-2 md:col-span-2">
          <label class="mb-0 text-heading-3 text-n-slate-12">
            {{ t('DEALS.FORM.CONTACT') }}
          </label>
          <div
            v-if="selectedContact"
            class="flex items-center justify-between rounded-lg bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
          >
            <span>
              {{ selectedContact.name }}
              <span class="text-n-slate-10">
                {{ selectedContact.email || selectedContact.phone_number }}
              </span>
            </span>
            <Button
              type="button"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="xs"
              :aria-label="t('GENERAL.CLOSE')"
              @click="selectedContact = null"
            />
          </div>
          <Input
            v-model="contactQuery"
            :label="t('DEALS.FORM.CONTACT_SEARCH')"
            :placeholder="t('DEALS.FORM.CONTACT_SEARCH_PLACEHOLDER')"
          />
          <p v-if="isSearchingContacts" class="mb-0 text-xs text-n-slate-10">
            {{ t('DEALS.FORM.CONTACT_SEARCHING') }}
          </p>
          <div
            v-else-if="contactQuery.trim().length >= 2"
            class="max-h-40 overflow-y-auto rounded-lg outline outline-1 outline-n-weak"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="flex w-full flex-col gap-0.5 px-3 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2"
              @click="selectContact(contact)"
            >
              <span>{{ contact.name }}</span>
              <span class="text-xs text-n-slate-10">
                {{
                  contact.email ||
                  contact.phone_number ||
                  t('DEALS.FORM.CONTACT_NO_DETAILS')
                }}
              </span>
            </button>
            <p
              v-if="!contactResults.length"
              class="mb-0 px-3 py-2 text-sm text-n-slate-10"
            >
              {{ t('DEALS.FORM.CONTACT_NO_RESULTS') }}
            </p>
          </div>
        </div>

        <label class="flex flex-col gap-1 text-heading-3 text-n-slate-12">
          {{ t('DEALS.FORM.STAGE') }}
          <select
            v-model="form.deal_stage_id"
            required
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option v-for="stage in stages" :key="stage.id" :value="stage.id">
              {{ stage.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-heading-3 text-n-slate-12">
          {{ t('DEALS.FORM.ASSIGNEE') }}
          <select
            v-model="form.assignee_id"
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="">{{ t('DEALS.FORM.NO_ASSIGNEE') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.available_name || agent.name }}
            </option>
          </select>
        </label>

        <Input
          v-model="form.value"
          type="number"
          min="0"
          step="0.01"
          :label="t('DEALS.FORM.VALUE')"
        />
        <Input v-model="form.currency" :label="t('DEALS.FORM.CURRENCY')" />

        <label class="flex flex-col gap-1 text-heading-3 text-n-slate-12">
          {{ t('DEALS.FORM.STATUS') }}
          <select
            v-model="form.status"
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="open">{{ t('DEALS.STATUS.OPEN') }}</option>
            <option value="won">{{ t('DEALS.STATUS.WON') }}</option>
            <option value="lost">{{ t('DEALS.STATUS.LOST') }}</option>
          </select>
        </label>
        <Input
          v-model="form.started_on"
          type="date"
          :label="t('DEALS.FORM.STARTED_ON')"
          required
        />
        <Input
          v-model="form.expected_close_on"
          type="date"
          :label="t('DEALS.FORM.EXPECTED_CLOSE_ON')"
        />
        <Input
          v-model="form.closed_on"
          type="date"
          :label="t('DEALS.FORM.CLOSED_ON')"
        />
        <Input
          v-model="form.valid_until"
          type="date"
          :label="t('DEALS.FORM.VALID_UNTIL')"
        />
        <TextArea
          v-model="form.description"
          class="md:col-span-2"
          :label="t('DEALS.FORM.DESCRIPTION')"
          :placeholder="t('DEALS.FORM.DESCRIPTION_PLACEHOLDER')"
          :max-length="5000"
        />
      </div>

      <template v-if="isEditing">
        <section class="flex flex-col gap-3 border-t border-n-weak pt-5">
          <h4 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('DEALS.COMMENTS.TITLE') }}
          </h4>
          <TextArea
            v-model="comment"
            :placeholder="t('DEALS.COMMENTS.PLACEHOLDER')"
            :max-length="1000"
            show-character-count
          />
          <Button
            type="button"
            class="self-end"
            :label="t('DEALS.COMMENTS.ADD')"
            :disabled="!comment.trim()"
            :is-loading="isAddingComment"
            @click="addComment"
          />
          <p
            v-if="!dealDetails.comments?.length"
            class="mb-0 text-sm text-n-slate-10"
          >
            {{ t('DEALS.COMMENTS.EMPTY') }}
          </p>
          <article
            v-for="item in dealDetails.comments"
            :key="item.id"
            class="rounded-lg bg-n-alpha-2 p-3"
          >
            <p class="mb-2 whitespace-pre-wrap text-sm text-n-slate-12">
              {{ item.content }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{
                t('DEALS.COMMON.USER_DATE', {
                  user:
                    item.author?.display_name || t('DEALS.COMMON.UNKNOWN_USER'),
                  date: formatDateTime(item.created_at),
                })
              }}
            </p>
          </article>
        </section>

        <section class="flex flex-col gap-3 border-t border-n-weak pt-5">
          <h4 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('DEALS.ACTIVITIES.TITLE') }}
          </h4>
          <p
            v-if="!dealDetails.activities?.length"
            class="mb-0 text-sm text-n-slate-10"
          >
            {{ t('DEALS.ACTIVITIES.EMPTY') }}
          </p>
          <div
            v-for="activity in dealDetails.activities"
            :key="activity.id"
            class="flex gap-3 text-sm"
          >
            <span class="mt-1 size-2 flex-shrink-0 rounded-full bg-n-slate-7" />
            <div>
              <p class="mb-0 text-n-slate-12">
                {{ activityLabel(activity.action) }}
              </p>
              <p class="mb-0 text-xs text-n-slate-10">
                {{
                  t('DEALS.COMMON.USER_DATE', {
                    user: activityActor(activity),
                    date: formatDateTime(activity.created_at),
                  })
                }}
              </p>
            </div>
          </div>
        </section>
      </template>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-between gap-3">
        <Button
          v-if="isEditing"
          type="button"
          color="ruby"
          variant="ghost"
          :label="t('DEALS.ACTIONS.DELETE')"
          :is-loading="isDeleting"
          @click="deleteDeal"
        />
        <span v-else />
        <div class="flex gap-3">
          <Button
            type="button"
            color="slate"
            variant="faded"
            :label="t('DEALS.ACTIONS.CANCEL')"
            @click="dialogRef.close()"
          />
          <Button
            type="button"
            :label="t('DEALS.ACTIONS.SAVE')"
            :disabled="isFormInvalid"
            :is-loading="isSaving"
            @click="saveDeal"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
